import axiosInstance from './axiosInstance';
import { fetchOnce } from '../utils/fetchOnce';

// ---------------------------------------------------------------------------
// Request DTOs — shapes the frontend actually sends to each endpoint.
// ---------------------------------------------------------------------------
type QueryParams = Record<string, string | number | boolean | undefined>;

type LoginRequest = { email: string; password: string };
type RegisterRequest = { email: string; password: string; firstName?: string; lastName?: string; businessName?: string; tenantDomain?: string };
type CreateBusinessDto = { name: string; businessType: string; email?: string; phone?: string; address?: string; city?: string; district?: string; panNumber?: string | null; isVatRegistered?: boolean; vatNumber?: string | null; defaultCurrency?: string | null; fiscalYearStart?: string | null; website?: string | null; description?: string | null };
type CreateBranchDto = { name: string; address?: string; phone?: string; isMain?: boolean };
type CreateKhataPartyDto = { name: string; phone?: string; type: number; openingBalance?: number; customerCreditLimit?: number; customerCreditDays?: number; customerOpeningBalance?: number; supplierCreditLimit?: number; supplierCreditDays?: number; supplierOpeningBalance?: number };
type CreateReminderDto = { partyId: string; message: string; via?: string };
type CreateContactDto = { name: string; businessName?: string | null; contactPerson?: string; panNumber?: string | null; vatNumber?: string; phone: string; landlinePhone?: string | null; email?: string | null; address?: string | null; city?: string; type: number; notes?: string | null; customerCreditLimit?: number; customerCreditDays?: number; customerOpeningBalance?: number; supplierCreditLimit?: number; supplierCreditDays?: number; supplierOpeningBalance?: number };
type CreateInvoiceDto = { customerId: string; customerName?: string; billTo?: string; issueDate: string; dueDate: string; paymentTerms?: string; buyerPAN?: string; bankDetails?: string; termsAndConditions?: string; isTaxInvoice?: boolean; applyVat?: boolean; notes?: string; items: Array<{ description: string; quantity: number; unitPrice: number }> };
type CreateExpenseDto = { title: string; category: string; amount: number; expenseDate: string; paymentMode: string; reference?: string; notes?: string; vendorName?: string; isRecurring?: boolean; recurringFrequency?: string };
type CreateBankAccountDto = { accountName: string; accountType: string; bankName?: string; accountNumber?: string; branchName?: string; openingBalance: number; isDefault?: boolean };
type CreateBankTransactionDto = { accountId: string; type: string; amount: number; transactionDate: string; description?: string; payee?: string; reference?: string; category?: string };
type CreateAccountingAccountDto = { code: string; name: string; description?: string; type: string; subType?: string; parentAccountId?: string; openingBalance?: number };
export type CreateManualJournalEntryDto = { entryDate: string; description: string; reference?: string; lines: Array<{ accountId: string; debit: number; credit: number; description?: string }> };
type CreateKhataEntryRequestDto = { partyId: string; type: number; transactionType: string; paymentMode: string; referenceNumber?: string; amount: number; note?: string; date?: string };

export type ChangePasswordRequest = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export type PartyLedgerEntryDto = {
  id: string;
  transactionDate: string;
  transactionType: string;
  referenceNumber: string;
  description: string;
  debit: number;
  credit: number;
  balance: number;
};

export type AgingSummaryDto = {
  current: number;
  thirtyOneToSixty: number;
  sixtyOneToNinety: number;
  ninetyPlus: number;
  total: number;
};

export type PartyLedgerDto = {
  partyId: string;
  partyName: string;
  currentBalance: number;
  aging: AgingSummaryDto;
  entries: PartyLedgerEntryDto[];
};

export type KhataEntryDto = {
  id: string;
  partyId: string;
  partyName: string;
  type: number;
  transactionType: string;
  paymentMode: string;
  referenceNumber?: string;
  amount: number;
  runningBalance: number;
  note?: string;
  date: string;
  createdAt: string;
  linkedModule?: string;
  linkedId?: string;
};

export type ContactDto = {
  id: string;
  name: string;
  code?: string;
  businessName?: string;
  contactPerson?: string;
  panNumber?: string;
  vatNumber?: string;
  phone?: string;
  landlinePhone?: string;
  email?: string;
  address?: string;
  city?: string;
  type: number;
  isActive: boolean;
  notes?: string;
  customerCreditLimit: number;
  customerCreditDays: number;
  customerOpeningBalance: number;
  customerCurrentBalance: number;
  supplierCreditLimit: number;
  supplierCreditDays: number;
  supplierOpeningBalance: number;
  supplierCurrentBalance: number;
  createdAt: string;
};

export type LedgerDateRange = { fromDate?: string; toDate?: string };

export const authApi = {
  register: (data: RegisterRequest) => axiosInstance.post('/auth/register', data),
  login: (data: LoginRequest) => axiosInstance.post('/auth/login', data),
  logout: (refreshToken: string) => axiosInstance.post('/auth/logout', { refreshToken }),
  changePassword: (data: ChangePasswordRequest) => axiosInstance.put('/auth/change-password', data),
  requestPasswordReset: (email: string) => axiosInstance.post('/auth/request-password-reset', { email }),
};

export const businessApi = {
  create: (data: CreateBusinessDto) => axiosInstance.post('/business/create', data),
  getMe: () => fetchOnce('business:me', () => axiosInstance.get('/business/me')),
  update: (data: Partial<CreateBusinessDto>) => axiosInstance.put('/business/update', data),
  createBranch: (data: CreateBranchDto) => axiosInstance.post('/business/branch/create', data),
  getBranches: () => axiosInstance.get('/business/branches'),
};

export const dashboardApi = {
  getDashboard: () => axiosInstance.get('/dashboard'),
  getOwner: () => axiosInstance.get('/dashboard/owner'),
  getFinance: () => axiosInstance.get('/dashboard/finance'),
  getHr: () => axiosInstance.get('/dashboard/hr'),
  getSales: () => axiosInstance.get('/dashboard/sales'),
  getPos: () => axiosInstance.get('/dashboard/pos'),
  getToday: () => axiosInstance.get('/dashboard/today'),
};

export const demoApi = {
  seed: () => axiosInstance.post('/demo/seed').then(r => r.data),
  seedDairy: () => axiosInstance.post('/demo/seed-dairy').then(r => r.data),
  clear: () => axiosInstance.post('/demo/clear').then(r => r.data),
};

export const bootstrapApi = {
  get: () => axiosInstance.get('/bootstrap'),
};

export const khataApi = {
  createParty: (data: CreateKhataPartyDto) => axiosInstance.post('/khata/party', data),
  getParties: () => axiosInstance.get('/khata/parties'),
  createEntry: (data: CreateKhataEntryRequestDto) => axiosInstance.post('/khata/entry', data),
  getEntries: (params?: QueryParams) => axiosInstance.get('/khata/entries', { params }),
  getBalance: (id: string) => axiosInstance.get(`/khata/party/${id}/balance`),
  createReminder: (data: CreateReminderDto) => axiosInstance.post('/khata/reminder', data),
  deleteParty: (id: string) => axiosInstance.delete(`/khata/party/${id}`),
  getStatementPdf: (id: string, params?: { from?: string; to?: string }) =>
    axiosInstance.get(`/khata/party/${id}/statement.pdf`, { responseType: 'blob', params: { dateFrom: params?.from, dateTo: params?.to } }),
};

export const contactApi = {
  create: (data: CreateContactDto) => axiosInstance.post('/contacts', data),
  getAll: (params?: QueryParams) => axiosInstance.get('/contacts', { params }),
  getById: (id: string) => axiosInstance.get(`/contacts/${id}`),
  update: (id: string, data: CreateContactDto) => axiosInstance.put(`/contacts/${id}`, data),
  delete: (id: string) => axiosInstance.delete(`/contacts/${id}`),
  getTransactions: (id: string) => axiosInstance.get(`/contacts/${id}/transactions`),
  getLedger: (id: string, params?: LedgerDateRange) =>
    axiosInstance.get(`/contacts/${id}/ledger`, { params: { fromDate: params?.fromDate, toDate: params?.toDate } }),
  getLedgerPdf: (id: string, params?: LedgerDateRange) =>
    axiosInstance.get(`/contacts/${id}/ledger.pdf`, { responseType: 'blob', params: { fromDate: params?.fromDate, toDate: params?.toDate } }),
};

export const invoiceApi = {
  create: (data: CreateInvoiceDto) => axiosInstance.post('/invoices', data),
  getAll: (params?: QueryParams) => axiosInstance.get('/invoices', { params }),
  getById: (id: string) => axiosInstance.get(`/invoices/${id}`),
  update: (id: string, data: Partial<CreateInvoiceDto>) => axiosInstance.put(`/invoices/${id}`, data),
  markPaid: (id: string) => axiosInstance.put(`/invoices/${id}/mark-paid`),
  send: (id: string) => axiosInstance.post(`/invoices/${id}/send`),
  getPdf: (id: string) => axiosInstance.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  delete: (id: string) => axiosInstance.delete(`/invoices/${id}`),
};

export const reportApi = {
  getSalesSummary: (params?: QueryParams) => axiosInstance.get('/reports/sales-summary', { params }),
  getProfitLoss: (params?: QueryParams) => axiosInstance.get('/reports/profit-loss', { params }),
  getOutstanding: () => axiosInstance.get('/reports/outstanding'),
  getVatReport: (params?: QueryParams) => axiosInstance.get('/reports/vat-report', { params }),
  getInventoryValuation: () => axiosInstance.get('/reports/inventory-valuation'),
  getCashFlow: (params?: QueryParams) => axiosInstance.get('/reports/cash-flow', { params }),
  getReceivablesAging: (asOf?: string) =>
    axiosInstance.get('/reports/receivables-aging', { params: asOf ? { asOf } : undefined }),
  getPayablesAging: (asOf?: string) =>
    axiosInstance.get('/purchase/reports/payables-aging', { params: asOf ? { asOf } : undefined }),
  getBalanceSheet: (params?: QueryParams) => axiosInstance.get('/reports/balance-sheet', { params }),
  getIncomeStatement: (params?: QueryParams) => axiosInstance.get('/reports/income-statement', { params }),
  getCashFlowStatement: (params?: QueryParams) => axiosInstance.get('/reports/cash-flow-statement', { params }),
  getTrialBalance: (params?: QueryParams) => axiosInstance.get('/reports/trial-balance', { params }),
  getGeneralLedger: (params: QueryParams) => axiosInstance.get('/reports/general-ledger', { params }),
  getBranchComparison: (params?: QueryParams) => axiosInstance.get('/reports/branch-comparison', { params }),
  getStockAging: (params?: QueryParams) => axiosInstance.get('/reports/stock-aging', { params }),
  getSales: (params?: QueryParams) => axiosInstance.get('/reports/sales', { params }),
  getPurchases: (params?: QueryParams) => axiosInstance.get('/reports/purchases', { params }),
  getInventoryReport: () => axiosInstance.get('/reports/inventory-report'),
  getEnhancedProfitLoss: (params?: QueryParams) => axiosInstance.get('/reports/profit-loss-enhanced', { params }),
  getVatReturn: (params?: QueryParams) => axiosInstance.get('/reports/vat-return', { params }),
  // BIZ-046: authoritative VAT return keyed to a Nepali B.S. fiscal year + B.S. month (1..12).
  // The API derives the AD [start,end) range from the B.S. month and returns the segregated
  // (standard / zero-rated / exempt) return (BIZ-016/BIZ-031).
  getVatReturnByBsPeriod: (fiscalYearBs: number, bsMonth: number) =>
    axiosInstance.get('/tax/reports/vat-return', { params: { fiscalYearBs, bsMonth } }),
  getTdsReturn: (params?: QueryParams) => axiosInstance.get('/reports/tds-return', { params }),
  getTaxFilingSummary: () => axiosInstance.get('/reports/tax-filing-summary'),
  getTopProducts: (from: string, to: string, limit?: number) =>
    axiosInstance.get('/pos/analytics/top-products', { params: { from, to, limit } }),
};

export const expensesApi = {
  create: (data: CreateExpenseDto) => axiosInstance.post('/expenses', data),
  getAll: (params?: QueryParams) => axiosInstance.get('/expenses', { params }),
  update: (id: string, data: Partial<CreateExpenseDto>) => axiosInstance.put(`/expenses/${id}`, data),
  delete: (id: string) => axiosInstance.delete(`/expenses/${id}`),
  getSummary: (params?: QueryParams) => axiosInstance.get('/expenses/summary', { params }),
  getCategories: () => axiosInstance.get('/expenses/categories'),
  postRecurring: (recurringTemplateId: string) =>
    axiosInstance.post(`/expenses/recurring/${recurringTemplateId}/post`),
};

// ---------------------------------------------------------------------------
// Copilot
// ---------------------------------------------------------------------------
export type CopilotEvaluateBody = {
  surface: string;
  path: string;
  form?: Record<string, unknown> | null;
  entityId?: string | null;
  isOffline?: boolean;
};

export const copilotApi = {
  evaluate: (body: CopilotEvaluateBody) =>
    axiosInstance.post('/copilot/evaluate', body).then((r) => r.data),
  dashboard: () =>
    axiosInstance.get('/copilot/dashboard').then((r) => r.data),
  feedback: (recommendationId: string, body: { outcome: string; finalPayloadJson?: string; comment?: string }) =>
    axiosInstance.post(`/copilot/recommendations/${recommendationId}/feedback`, body).then((r) => r.data),
};

export const bankingApi = {
  createAccount: (data: CreateBankAccountDto) => axiosInstance.post('/banking/accounts', data),
  getAccounts: () => axiosInstance.get('/banking/accounts'),
  updateAccount: (id: string, data: Partial<CreateBankAccountDto>) => axiosInstance.put(`/banking/accounts/${id}`, data),
  deleteAccount: (id: string) => axiosInstance.delete(`/banking/accounts/${id}`),
  createTransaction: (data: CreateBankTransactionDto) => axiosInstance.post('/banking/transactions', data),
  getTransactions: (params?: QueryParams) => axiosInstance.get('/banking/transactions', { params }),
  deleteTransaction: (id: string) => axiosInstance.delete(`/banking/transactions/${id}`),
  getCashBook: (params?: QueryParams) => axiosInstance.get('/banking/cashbook', { params }),
  getSummary: () => axiosInstance.get('/banking/summary'),
};

export const accountingApi = {
  getAccounts: () => axiosInstance.get('/accounting/accounts').then(r => r.data),
  getAccountTree: () => axiosInstance.get('/accounting/accounts/tree').then(r => r.data),
  getAccount: (id: string) => axiosInstance.get(`/accounting/accounts/${id}`).then(r => r.data),
  createAccount: (data: CreateAccountingAccountDto) => axiosInstance.post('/accounting/accounts', data).then(r => r.data),
  updateAccount: (id: string, data: Partial<CreateAccountingAccountDto>) => axiosInstance.put(`/accounting/accounts/${id}`, data).then(r => r.data),
  deleteAccount: (id: string) => axiosInstance.delete(`/accounting/accounts/${id}`).then(r => r.data),
  getAccountBalance: (id: string, asOfDate?: string) =>
    axiosInstance.get(`/accounting/accounts/balance/${id}`, { params: asOfDate ? { asOfDate } : undefined }).then(r => r.data),
  getJournalEntries: (params?: QueryParams) => axiosInstance.get('/accounting/journal-entries', { params }).then(r => r.data),
  getJournalEntry: (id: string) => axiosInstance.get(`/accounting/journal-entries/${id}`).then(r => r.data),
  createManualJournalEntry: (data: CreateManualJournalEntryDto) => axiosInstance.post('/accounting/journal-entries', data).then(r => r.data),
  reverseJournalEntry: (id: string, reason: string) =>
    axiosInstance.post(`/accounting/journal-entries/${id}/reverse`, { reason }).then(r => r.data),
  getNextEntryNumber: () => axiosInstance.get('/accounting/journal-entries/next-number').then(r => r.data),
};

export type AccountDto = {
  id: string;
  code: string;
  name: string;
  description?: string;
  type: 'Asset' | 'Liability' | 'Equity' | 'Revenue' | 'Expense';
  subType: string;
  parentAccountId?: string;
  openingBalance: number;
  currentBalance: number;
  isSystemAccount: boolean;
  isActive: boolean;
  children: AccountDto[];
};

export type JournalEntryDto = {
  id: string;
  entryNumber: string;
  entryDate: string;
  description: string;
  reference?: string;
  sourceModule: string;
  status: 'Posted' | 'Draft' | 'Reversed';
  totalDebit: number;
  totalCredit: number;
  lines: JournalEntryLineDto[];
};

export type JournalEntryLineDto = {
  id: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  debit: number;
  credit: number;
  description?: string;
};

export type JournalEntryListDto = {
  id: string;
  entryNumber: string;
  entryDate: string;
  description: string;
  sourceModule: string;
  status: string;
  totalDebit: number;
  totalCredit: number;
};
